/* eslint-disable max-lines */
import { MetricTriggerType, SeverityLevel, ThresholdOperator, UnifiedAlertSource } from '@logz-pkg/enums';
import { MetricAlertModel } from './legacy/alert';
import { MetricQueryModel, MetricTrigger } from './sub-component';
export const CONDITION_REF_ID = 'logz-condition';
class GrafanaAlertManipulator {
    DATASOURCE_EXPR = '__expr__';
    convertFromGrafanaAlertRule(grafanaRule) {
        const rule = grafanaRule.grafana_alert || grafanaRule;
        const alert = new MetricAlertModel();
        alert.source = UnifiedAlertSource.Metrics;
        alert.id = rule.id;
        alert.uid = rule.uid;
        alert.title = rule.title;
        alert.updatedAt = rule.updated;
        alert.description = rule.annotations?.logzio_description || '';
        alert.tags = this.parseTags(rule.annotations?.logzio_tags);
        alert.severityLevel = rule.annotations?.logzio_severity || SeverityLevel.Medium;
        alert.linkedFolder = rule.annotations?.logzio_folder_uid;
        alert.linkedDashboard = rule.annotations?.logzio_dashboard_uid;
        alert.linkedPanel = rule.annotations?.logzio_panel_uid;
        alert.rca = rule.annotations?.__logzioAlertRCA__ === 'on';
        alert.useAlertNotificationEndpointsForRca = rule.annotations?.logzio_useAlertNotificationEndpointsForRca === 'on';
        alert.rcaNotificationEndpointIds = rule.annotations?.logzio_notification_endpoints
            ? rule.annotations.logzio_notification_endpoints.split(',').map(id => parseInt(id, 10))
            : [];
        alert.rcaNotificationEmails = rule.annotations?.logzio_notification_emails
            ? rule.annotations.logzio_notification_emails
                .split(',')
                .map(email => email.trim())
                .filter(Boolean)
            : [];
        alert.runbook = rule.annotations?.logzio_runbook_url;
        const rawDailyLimit = rule.annotations?.logzio_daily_invocation_limit;
        alert.dailyInvocationLimit =
            rawDailyLimit !== null && rawDailyLimit !== undefined && rawDailyLimit !== '' && !isNaN(Number(rawDailyLimit))
                ? Number(rawDailyLimit)
                : null;
        const rawSoftDailyLimit = rule.annotations?.logzio_soft_daily_invocation_limit;
        alert.softDailyInvocationLimit =
            rawSoftDailyLimit !== null &&
                rawSoftDailyLimit !== undefined &&
                rawSoftDailyLimit !== '' &&
                !isNaN(Number(rawSoftDailyLimit))
                ? Number(rawSoftDailyLimit)
                : null;
        alert.searchTimeFrameMinutes = this.parseForDuration(grafanaRule.for || rule.for || '15m');
        alert.isEnabled = !rule.isPaused;
        const { queries, conditionRefId, trigger } = this.parseQueriesAndTrigger(rule);
        alert.queries = queries;
        alert.conditionRefId = conditionRefId;
        alert.trigger = trigger;
        alert.output = {
            suppressNotificationsMinutes: 10,
            type: 'json',
            recipients: {
                emails: this.parseEmails(rule.annotations?.logzio_emails),
                notificationEndpoints: this.parseEndpoints(rule.annotations?.logzio_endpoints_ids),
            },
        };
        return alert;
    }
    buildDataArray(alert) {
        const queries = [];
        alert.queries.forEach(query => {
            if (query.queryDefinition.promql) {
                queries.push({
                    refId: query.refId,
                    queryType: '',
                    relativeTimeRange: {
                        from: alert.searchTimeFrameMinutes * 60,
                        to: 0,
                    },
                    datasourceUid: query.queryDefinition.dataSource,
                    model: {
                        editorMode: 'code',
                        expr: query.queryDefinition.promql,
                        instant: true,
                        intervalMs: 1000,
                        legendFormat: '__auto',
                        maxDataPoints: 43200,
                        range: false,
                        refId: query.refId,
                        datasource: {
                            uid: query.queryDefinition.dataSource,
                            type: 'prometheus',
                        },
                    },
                });
            }
        });
        const { trigger } = alert;
        if (trigger?.triggerType === MetricTriggerType.THRESHOLD) {
            queries.push(this.buildThresholdQuery(alert, trigger));
        }
        else if (trigger?.triggerType === MetricTriggerType.MATH) {
            queries.push(this.buildMathQuery(alert, trigger));
        }
        return queries;
    }
    buildThresholdQuery(alert, trigger) {
        const refId = CONDITION_REF_ID;
        const { metricOperator, threshold, minThreshold, maxThreshold } = trigger;
        let evaluatorType = 'gt';
        let params = [0];
        const operatorHandlers = {
            [ThresholdOperator.ABOVE]: () => {
                evaluatorType = 'gt';
                params = [threshold ?? 0];
            },
            [ThresholdOperator.BELOW]: () => {
                evaluatorType = 'lt';
                params = [threshold ?? 0];
            },
            [ThresholdOperator.WITHIN_RANGE]: () => {
                evaluatorType = 'within_range';
                params = [minThreshold ?? 0, maxThreshold ?? 0];
            },
            [ThresholdOperator.OUTSIDE_RANGE]: () => {
                evaluatorType = 'outside_range';
                params = [minThreshold ?? 0, maxThreshold ?? 0];
            },
        };
        if (metricOperator && operatorHandlers[metricOperator]) {
            operatorHandlers[metricOperator]();
        }
        else {
            evaluatorType = 'gt';
            params = [0, 0];
        }
        const queryRefId = alert.queries[0].refId || 'A';
        return {
            refId,
            queryType: '',
            relativeTimeRange: {
                from: alert.searchTimeFrameMinutes * 60,
                to: 0,
            },
            datasourceUid: this.DATASOURCE_EXPR,
            model: {
                refId,
                datasource: {
                    type: this.DATASOURCE_EXPR,
                    uid: this.DATASOURCE_EXPR,
                },
                type: 'threshold',
                conditions: [
                    {
                        type: 'query',
                        reducer: {
                            params: [],
                            type: 'last',
                        },
                        operator: {
                            type: 'and',
                        },
                        query: {
                            params: [queryRefId],
                        },
                        evaluator: {
                            params,
                            type: evaluatorType,
                        },
                    },
                ],
                expression: queryRefId,
                intervalMs: 1000,
                maxDataPoints: 43200,
            },
        };
    }
    buildMathQuery(alert, trigger) {
        const refId = CONDITION_REF_ID;
        return {
            refId,
            queryType: '',
            relativeTimeRange: {
                from: alert.searchTimeFrameMinutes * 60,
                to: 0,
            },
            datasourceUid: this.DATASOURCE_EXPR,
            model: {
                refId,
                datasource: {
                    name: 'Expression',
                    type: this.DATASOURCE_EXPR,
                    uid: this.DATASOURCE_EXPR,
                },
                type: 'math',
                conditions: [
                    {
                        type: 'query',
                        reducer: {
                            params: [],
                            type: 'avg',
                        },
                        operator: {
                            type: 'and',
                        },
                        query: {
                            params: [],
                        },
                        evaluator: {
                            params: [0, 0],
                            type: 'gt',
                        },
                    },
                ],
                expression: trigger.mathExpression || '',
                intervalMs: 1000,
                maxDataPoints: 43200,
            },
        };
    }
    buildAnnotations(alert) {
        const annotations = {};
        if (alert.description) {
            annotations.logzio_description = alert.description;
        }
        // Recipients - format as JSON arrays in strings
        if (alert.output.recipients.emails.length > 0) {
            // Format as ['email1@example.com','email2@example.com']
            annotations.logzio_emails = JSON.stringify(alert.output.recipients.emails);
        }
        if (alert.output.recipients.notificationEndpoints.length > 0) {
            // Format as [1, 2, 3]
            const endpointIds = alert.output.recipients.notificationEndpoints.map(e => e.id);
            annotations.logzio_endpoints_ids = JSON.stringify(endpointIds);
        }
        if (alert.linkedFolder)
            annotations.logzio_folder_uid = alert.linkedFolder;
        if (alert.linkedDashboard)
            annotations.logzio_dashboard_uid = alert.linkedDashboard;
        if (alert.linkedPanel)
            annotations.logzio_panel_uid = alert.linkedPanel;
        if (alert.severityLevel) {
            annotations.logzio_severity = alert.severityLevel;
        }
        // Tags - format as ['tag1','tag2','tag3']
        if (alert.tags.length > 0) {
            annotations.logzio_tags = JSON.stringify(alert.tags);
        }
        if (alert.rca) {
            annotations.__logzioAlertRCA__ = 'on';
        }
        else {
            annotations.__logzioAlertRCA__ = 'off';
        }
        if (alert.runbook) {
            annotations.logzio_runbook_url = alert.runbook;
        }
        if (alert.dailyInvocationLimit !== undefined && alert.dailyInvocationLimit !== null) {
            annotations.logzio_daily_invocation_limit = String(alert.dailyInvocationLimit);
        }
        else {
            annotations.logzio_daily_invocation_limit = '';
        }
        if (alert.softDailyInvocationLimit !== undefined && alert.softDailyInvocationLimit !== null) {
            annotations.logzio_soft_daily_invocation_limit = String(alert.softDailyInvocationLimit);
        }
        else {
            annotations.logzio_soft_daily_invocation_limit = '';
        }
        if (alert.rcaNotificationEndpointIds) {
            annotations.logzio_notification_endpoints = alert.rcaNotificationEndpointIds.join(',');
        }
        if (alert.rcaNotificationEmails && alert.rcaNotificationEmails.length > 0) {
            annotations.logzio_notification_emails = alert.rcaNotificationEmails.join(',');
        }
        if (alert.useAlertNotificationEndpointsForRca) {
            annotations.logzio_useAlertNotificationEndpointsForRca = 'on';
            annotations.logzio_notification_endpoints = '';
        }
        else {
            annotations.logzio_useAlertNotificationEndpointsForRca = 'off';
        }
        annotations.logzio_condition_text = this.buildConditionText(alert);
        return annotations;
    }
    buildConditionText(alert) {
        const { trigger } = alert;
        const timeFrame = alert.searchTimeFrameMinutes;
        if (!trigger)
            return '';
        if (trigger.triggerType === MetricTriggerType.MATH) {
            return `${trigger.mathExpression} for ${timeFrame} min`;
        }
        const { metricOperator, threshold, minThreshold, maxThreshold } = trigger;
        const operatorTextMap = {
            [ThresholdOperator.ABOVE]: () => `> ${threshold} for ${timeFrame} min`,
            [ThresholdOperator.BELOW]: () => `< ${threshold} for ${timeFrame} min`,
            [ThresholdOperator.WITHIN_RANGE]: () => `between ${minThreshold} and ${maxThreshold} for ${timeFrame} min`,
            [ThresholdOperator.OUTSIDE_RANGE]: () => `outside ${minThreshold} to ${maxThreshold} for ${timeFrame} min`,
        };
        return operatorTextMap[metricOperator]?.() || '';
    }
    parseQueriesAndTrigger(rule) {
        const queries = [];
        const trigger = new MetricTrigger();
        let conditionRefId = 'A';
        if (!rule.data) {
            const defaultQuery = new MetricQueryModel();
            defaultQuery.refId = 'A';
            return { queries: [defaultQuery], conditionRefId, trigger };
        }
        const conditionQuery = rule.data.find((q) => q.refId === rule.condition);
        const metricQueries = rule.data.filter((q) => q.datasourceUid !== this.DATASOURCE_EXPR);
        metricQueries.forEach((query) => {
            const metricQuery = new MetricQueryModel();
            metricQuery.refId = query.refId;
            metricQuery.queryDefinition.dataSource = query.datasourceUid;
            metricQuery.queryDefinition.promql = query.model?.expr || '';
            queries.push(metricQuery);
        });
        if (conditionQuery) {
            const { model } = conditionQuery;
            trigger.triggerType = model?.type === 'math' ? MetricTriggerType.MATH : MetricTriggerType.THRESHOLD;
            if (trigger.triggerType === MetricTriggerType.MATH) {
                trigger.mathExpression = model?.expression || '';
            }
            else {
                const condition = model?.conditions?.[0];
                if (condition) {
                    const evaluatorType = condition.evaluator?.type;
                    const params = condition.evaluator?.params || [];
                    const evaluatorHandlers = {
                        gt: () => {
                            trigger.metricOperator = ThresholdOperator.ABOVE;
                            trigger.threshold = params[0] || 0;
                        },
                        lt: () => {
                            trigger.metricOperator = ThresholdOperator.BELOW;
                            trigger.threshold = params[0] || 0;
                        },
                        within_range: () => {
                            trigger.metricOperator = ThresholdOperator.WITHIN_RANGE;
                            trigger.minThreshold = params[0] || 0;
                            trigger.maxThreshold = params[1] || 0;
                        },
                        outside_range: () => {
                            trigger.metricOperator = ThresholdOperator.OUTSIDE_RANGE;
                            trigger.minThreshold = params[0] || 0;
                            trigger.maxThreshold = params[1] || 0;
                        },
                    };
                    if (evaluatorType && evaluatorHandlers[evaluatorType]) {
                        evaluatorHandlers[evaluatorType]();
                    }
                    const queryRef = condition.query?.params?.[0];
                    if (queryRef) {
                        conditionRefId = queryRef;
                    }
                }
            }
        }
        if (queries.length === 0) {
            const defaultQuery = new MetricQueryModel();
            defaultQuery.refId = 'A';
            queries.push(defaultQuery);
        }
        return { queries, conditionRefId, trigger };
    }
    parseForDuration(forDuration) {
        const match = forDuration.match(/(\d+)([mh])/);
        if (match) {
            const value = parseInt(match[1], 10);
            const unit = match[2];
            return unit === 'h' ? value * 60 : value;
        }
        return 15;
    }
    parseTags(tagsStr) {
        if (!tagsStr)
            return [];
        try {
            return JSON.parse(tagsStr);
        }
        catch {
            return tagsStr.split(',').map(tag => tag.trim());
        }
    }
    parseEmails(emailsStr) {
        if (!emailsStr)
            return [];
        try {
            return JSON.parse(emailsStr);
        }
        catch {
            return emailsStr.split(',').map(email => email.trim());
        }
    }
    parseEndpoints(endpointsStr) {
        if (!endpointsStr)
            return [];
        try {
            return JSON.parse(endpointsStr);
        }
        catch {
            return endpointsStr
                .split(',')
                .map(id => id.trim())
                .filter(id => /^\d+$/.test(id))
                .map(id => ({ id: Number(id) }));
        }
    }
}
export const grafanaAlertManipulator = new GrafanaAlertManipulator();
//# sourceMappingURL=grafana-alert.manipulator.js.map